/* Sham Source Nav - highlights the category currently in view */

class ShamSourceNav extends HTMLElement {
  connectedCallback() {
    this.links = Array.from(this.querySelectorAll('[data-sham-source-nav-link]'));
    if (this.links.length === 0) return;

    this.targets = this.links
      .map((link) => document.getElementById(link.dataset.shamSourceNavLink))
      .filter(Boolean);
    if (this.targets.length === 0) return;

    this.publishHeight();
    this.trackHeader();
    this.observeTargets();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
  }

  // The legacy header hides on scroll-down, so the space it occupies swings
  // between 0 and its full height while --header-height stays constant. Parking
  // the bar at that constant leaves a strip of scrolling page above it. Measure
  // the header's live bottom edge instead of reading its classes, so this keeps
  // working if the legacy implementation changes.
  trackHeader() {
    // The section-group wrapper collapses to zero height, so its rect is not the
    // header's. Take the first candidate that actually occupies space.
    const candidates = ['sticky-header', '.header-wrapper', '.shopify-section-group-header-group'];

    let queued = false;
    const write = () => {
      queued = false;
      let bottom = 0;
      for (const selector of candidates) {
        const el = document.querySelector(selector);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.height > 0) {
          bottom = Math.max(0, Math.round(rect.bottom));
          break;
        }
      }
      document.documentElement.style.setProperty('--sham-source-nav-offset', `${bottom}px`);
    };
    this.onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(write);
    };

    write();
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  // scroll-margin-top on the anchored sections needs this bar's height, which
  // changes with viewport and label wrapping. header.liquid publishes
  // --header-height the same way.
  publishHeight() {
    const write = () => {
      document.documentElement.style.setProperty('--sham-source-nav-height', `${this.offsetHeight}px`);
    };
    write();
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(write);
      this.resizeObserver.observe(this);
    }
  }

  setCurrent(id) {
    this.links.forEach((link) => {
      link.classList.toggle('is-current', link.dataset.shamSourceNavLink === id);
    });
  }

  // Sections are taller than the viewport, so "most visible" is the only stable
  // reading — a threshold on entry flips to the next category while most of the
  // previous one is still on screen.
  observeTargets() {
    const ratios = new Map();

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          ratios.set(entry.target.id, entry.isIntersecting ? entry.intersectionRatio : 0);
        });

        let best = null;
        let bestRatio = 0;
        ratios.forEach((ratio, id) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            best = id;
          }
        });

        if (best) this.setCurrent(best);
      },
      {
        rootMargin: `-${this.offsetHeight}px 0px 0px 0px`,
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      }
    );

    this.targets.forEach((target) => this.observer.observe(target));
  }
}

customElements.define('sham-source-nav', ShamSourceNav);
